import { t } from "@lingui/macro";
import { setTag, setUser } from "@sentry/react";
import {
  DocumentLimitTypeEnum,
  LoginDocument,
  type LoginQuery,
} from "@src/__generated__/urql-graphql";
import { API_BASE_URL } from "@src/config/constants";
import type { TPages } from "@src/config/routes";
import { trackEvent } from "@src/services/amplitude";
import { client } from "@src/services/client";
import type { AppStore } from "@src/stores/AppStore";
import { TwoFactorAuthenticationStore } from "@src/stores/AuthStore/2FactorAuthenticationStore";
import type { BaseStore } from "@src/stores/BaseStore";
import { ForgottenPasswordFormState } from "@src/stores/forms/ForgottenPasswordFormState";
import { InitialPasswordFormState } from "@src/stores/forms/InitialPasswordFormState";
import { LoginFormState } from "@src/stores/forms/LoginFormState";
import { can } from "@src/utils/components/permissions";
import { currency } from "@src/utils/formatters";
import { action, computed, makeObservable, observable } from "mobx";
import Router from "next/router";
import { type Route, route } from "nextjs-routes";
import { Me } from "../models/Me";

export class AuthStore implements BaseStore {
  appStore: AppStore;
  twoFactorAuthenticationStore: TwoFactorAuthenticationStore;
  readonly PUBLIC_PAGES: Route["pathname"][] = [
    "/accept_email_delivery",
    "/auth/two-factor/challenge",
    "/auth/two-factor/confirm",
    "/auth/two-factor/enable",
    "/auth/two-factor/success",
    "/quote-approval",
    "/forgot-password",
    "/set-password",
    "/login",
  ];

  constructor(appStore: AppStore) {
    makeObservable(this);
    this.appStore = appStore;
    this.twoFactorAuthenticationStore = new TwoFactorAuthenticationStore(
      appStore,
    );
    this.initialPasswordForm = new InitialPasswordFormState(appStore);
  }

  @computed get isInternalUser() {
    return this.user?.type === "internal";
  }

  private tokenAbortController = new AbortController();
  async getCsrfToken() {
    try {
      // Abort previous request to prevent race condition.
      this.tokenAbortController.abort("New token was requested"); // eslint-disable-line lingui/no-unlocalized-strings

      this.tokenAbortController = new AbortController();
      const response = await fetch(`${API_BASE_URL}/fe-get-token`, {
        credentials: "include",
        signal: this.tokenAbortController.signal,
      });
      const data = (await response.json()) as { token: string };
      return data.token;
    } catch (e) {
      console.error(e);
    }

    return undefined;
  }

  async apiRequest(
    endpoint: string,
    body?: Record<string | number | symbol, unknown>,
    method: string = "POST",
  ) {
    return await fetch(`${API_BASE_URL}${endpoint}`, {
      credentials: "include",
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });
  }

  /*
   * Authentication
   */
  @observable authenticationStatus: "loading" | "error" | "success" = "loading";
  @observable authenticationStatusCode?: number;
  @observable.ref user?: Me;

  @action.bound async handleOtl() {
    if (!location.search.includes("otl=")) return;
    const otlHash = location.search.split("=")[1];
    const token = await this.getCsrfToken();

    if (otlHash && otlHash.length > 0 && token) {
      this.loginForm._token = token;
      try {
        await this.apiRequest("/otl", {
          hash: otlHash,
          _token: token,
        });
      } catch (e) {
        console.error(e);
        this.appStore.UIStore.toast({
          status: "error",
          title: t`Automatic login failed, please login with your credentials.`,
        });
      }
    }
  }

  @action.bound async authenticate() {
    this.setAuthenticationStatus("loading");

    await this.handleOtl();

    const { data, error } = await client.query<LoginQuery>(
      LoginDocument,
      {},
      {
        fetch: async (...args) => {
          const response = await fetch(...args);
          this.setAuthenticationStatusCode(response.status);

          if (response.status === 401) {
            Router.push({
              pathname: route({ pathname: "/login" }),
              query: { redirect_to: location.href },
            });
          }

          return response;
        },
        requestPolicy: "cache-first",
      },
    );

    if (error || !data) {
      this.setAuthenticationStatus("error");
      return;
    }

    if (data.me) {
      this.user = new Me(data.me);
      // NOTE: Columns need to be generated after user is set, because columns are based on user type which we don't know on app initialization
      this.appStore.tasksListingStore.columnsStore.generateColumns();
    }

    setUser({
      id: this.user?.id,
      email: this.user?.email,
      username: this.user?.full_name,
    });
    setTag("allfred_workspace", window.location.host.split(".")[0]);

    this.appStore.workspaceStore.from(data);

    trackEvent("login");

    if ("function" === typeof window.FreshworksWidget) {
      window.FreshworksWidget("identify", "ticketForm", {
        name: this.user?.full_name,
        email: this.user?.email,
      });
    }

    this.setAuthenticationStatus("success");

    this.appStore.notificationsStore.startTrackingNotifications();
  }

  @action.bound private setAuthenticationStatus(
    status: typeof AuthStore.prototype.authenticationStatus,
  ) {
    this.authenticationStatus = status;
  }

  @action.bound private setAuthenticationStatusCode(
    code: typeof AuthStore.prototype.authenticationStatusCode,
  ) {
    this.authenticationStatusCode = code;
  }

  /*
   * Login
   */
  loginForm = new LoginFormState();
  @observable remoteLoginUrl?: string;

  @action.bound setLoginFormState(
    state: typeof LoginFormState.prototype.state,
  ) {
    this.loginForm.state = state;
  }

  async initLogin(redirect_to?: string) {
    this.setLoginFormState("loading");

    if (redirect_to) {
      this.loginForm.redirect_to = redirect_to;
    }

    // this.setLoginFormCsrfToken();
    this.setLoginFormState("ready");

    try {
      const response = await this.apiRequest(
        "/remote-login-url",
        undefined,
        "GET",
      );

      const data = (await response.json()) as {
        url: string;
      };

      this.remoteLoginUrl = data.url;
    } catch {}
  }

  async setLoginFormCsrfToken() {
    const token = await this.getCsrfToken();
    if (!token) {
      this.setLoginFormState("session-error");
      return;
    }
    this.loginForm._token = token;
    this.setLoginFormState("ready");
  }

  async logIn() {
    this.setLoginFormState("submitting");

    await this.loginForm.form.validate();

    try {
      const response = await this.apiRequest("/fe-login", {
        _token: this.loginForm._token,
        redirect_to: this.loginForm.redirect_to || undefined,
        email: this.loginForm.email.$,
        password: this.loginForm.password.$,
        remember: this.loginForm.remember.$,
      });

      if (response.status === 200) {
        const data = (await response.json()) as
          | { two_factor: boolean }
          | { status: string };

        // If `two_factor` is present, user has two-factor authentication enabled.
        if ("two_factor" in data) {
          Router.push({
            pathname: route({ pathname: "/auth/two-factor/challenge" }),
            query: { redirect_to: this.loginForm.redirect_to },
          });
          return;
        }

        // Otherwise user is logged in and we can redirect to the requested page.
        window.location.href = this.loginForm.redirect_to || "/";
        return;
      }

      const data = (await response.json()) as {
        message: string;
        errors?: {
          email?: string[];
          password?: string[];
        };
      };

      if (data.errors) {
        if (data.errors.email) {
          this.loginForm.email.setError(data.errors.email[0]);
        }
        if (data.errors.password) {
          this.loginForm.password.setError(data.errors.password[0]);
        }

        if (this.loginForm.form.hasError) {
          await this.setLoginFormCsrfToken();
          return;
        }
      }
    } catch {}

    this.setLoginFormState("error");
  }

  /*
   * Forgotten password
   */
  forgottenPasswordForm = new ForgottenPasswordFormState();

  @action.bound setForgottenPasswordForm(
    state: typeof ForgottenPasswordFormState.prototype.state,
  ) {
    this.forgottenPasswordForm.state = state;
  }

  async initForgottenPassword() {
    // this.setForgottenPasswordForm("loading");
    // await this.setForgottenPasswordFormCsrfToken();
    this.setForgottenPasswordForm("ready");
  }

  async setForgottenPasswordFormCsrfToken() {
    const token = await this.getCsrfToken();
    if (!token) {
      this.setForgottenPasswordForm("error");
      return;
    }
    this.forgottenPasswordForm._token = token;
    this.setForgottenPasswordForm("ready");
  }

  async sendPasswordResetLink() {
    this.setForgottenPasswordForm("submitting");

    try {
      const response = await this.apiRequest("/forgot-password", {
        _token: this.forgottenPasswordForm._token,
        email: this.forgottenPasswordForm.email.$,
      });

      if (response.status < 300) {
        this.setForgottenPasswordForm("success");
        this.forgottenPasswordForm.form.reset();
        return;
      }

      const data = (await response.json()) as {
        message: string;
        errors?: {
          email?: string[];
        };
      };

      if (data.errors?.email) {
        this.forgottenPasswordForm.email.setError(data.errors.email[0]);
        await this.setForgottenPasswordFormCsrfToken();
        return;
      }
    } catch {}

    this.setForgottenPasswordForm("error");
  }

  initialPasswordForm: InitialPasswordFormState;

  /**
   * Authorization
   */
  hasPermissionForPage(_module: TPages) {
    const modulePermissions =
      this.appStore.workspaceStore.ROUTES_PERMISSION_MAP[_module];
    if (!modulePermissions) return false;
    return can(modulePermissions);
  }

  /**
   * Document Limits
   */

  /**
   * Get the document limit for a specific document type from user's roles.
   * Returns undefined if no limit exists (meaning unlimited access).
   * If multiple roles have limits for the same type, uses the lowest limit.
   */
  private getDocumentLimit(
    documentType: DocumentLimitTypeEnum,
  ): number | undefined {
    const roles = this.user?.roles;
    if (!roles) return undefined;

    let lowestLimit: number | undefined;

    for (const role of roles) {
      const limits = role.documentLimits;
      if (!limits) continue;

      for (const limit of limits) {
        if (limit.document_type === documentType) {
          const limitValue = Number(limit.limit);
          if (lowestLimit === undefined || limitValue < lowestLimit) {
            lowestLimit = limitValue;
          }
        }
      }
    }

    return lowestLimit;
  }

  /**
   * Check if user can perform action for a given document amount.
   * Returns true if no limit exists (unlimited) or amount is within limit.
   */
  private canPerformDocumentAction(
    documentType: DocumentLimitTypeEnum,
    amount: number,
  ): boolean {
    const limit = this.getDocumentLimit(documentType);
    // No limit defined = unlimited access
    if (limit === undefined) return true;
    return amount <= limit;
  }

  /**
   * Get complete document limit status for UI.
   * Returns all values needed for displaying limit information in components.
   */
  getDocumentLimitStatus(
    documentType: DocumentLimitTypeEnum,
    amount: number,
  ): {
    allowed: boolean;
    limit?: number;
    formattedLimit: string;
    formattedTotal: string;
    tooltipLabel: string;
  } {
    const documentTypeLabel = documentType.replaceAll("_", " ").toLowerCase();
    const limit = this.getDocumentLimit(documentType);
    const allowed = this.canPerformDocumentAction(documentType, amount);
    const formattedLimit = currency.formatByWorkspace(limit ?? 0);
    const formattedTotal = currency.formatByWorkspace(amount);

    let tooltipLabel: string;
    if (documentType === DocumentLimitTypeEnum.Expense) {
      tooltipLabel = t`You cannot approve ${documentTypeLabel}s above ${formattedLimit}. Current total: ${formattedTotal}`;
    } else {
      tooltipLabel = t`You cannot create or edit ${documentTypeLabel}s above ${formattedLimit}. Current total: ${formattedTotal}`;
    }

    return {
      allowed,
      limit,
      formattedLimit,
      formattedTotal,
      tooltipLabel,
    };
  }
}
